import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../context/CurrencyContext.tsx";
import { useTheme } from "../context/ThemeContext.tsx";
import { NamedValue } from "../shared/CaseModel";
import { percentage } from "../shared/percentage";
import randomId from "../shared/randomId";
import { getValue } from "../shared/WeightedValue";
import { AssetEditorDialog } from "./AssetEditorDialog";
import { TABULAR_NUMERALS } from "./general/dataAtoms/shared";
import { COLUMN_HEADING } from "./general/tableStyles";
import { YesNoDialog } from "./general/YesNoDialog";

const AssetRow = ({
  asset,
  onChange,
  onDelete,
  readOnly,
}: {
  asset: NamedValue;
  onChange: (updatedAsset: NamedValue) => void;
  onDelete: () => void;
  readOnly: boolean;
}) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { currency } = useCurrency();
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.parties.assets",
  });

  const totalWeight = asset.values.reduce((sum, { weight }) => sum + weight, 0);
  // a single estimate carries the whole weight, so its share says nothing
  const estimates =
    asset.values.length === 1
      ? t("oneEstimate")
      : asset.values
          .map(({ value, weight }) =>
            t("estimate", {
              value: currency.format(value),
              share: percentage.format(
                totalWeight > 0 ? weight / totalWeight : 0,
              ),
            }),
          )
          .join(" · ");

  return (
    <TableRow>
      <TableCell sx={{ fontWeight: 600 }}>{asset.name}</TableCell>
      <TableCell sx={{ color: "text.secondary", ...TABULAR_NUMERALS }}>
        {estimates}
      </TableCell>
      <TableCell
        align="right"
        sx={{ fontWeight: 700, whiteSpace: "nowrap", ...TABULAR_NUMERALS }}
      >
        {currency.format(getValue(asset.values).toNumber())}
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: "nowrap", py: 0.5 }}>
        {/* each tooltip needs an element that fires events, which a disabled
            button does not */}
        <Tooltip title={t("tip.edit")}>
          <span>
            <IconButton
              size="small"
              aria-label={t("tip.edit")}
              disabled={readOnly}
              onClick={() => setEditDialogOpen(true)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t("tip.delete")}>
          <span>
            <IconButton
              size="small"
              aria-label={t("tip.delete")}
              disabled={readOnly}
              onClick={() => setDeleteDialogOpen(true)}
              sx={{ "&:hover": { color: theme.palette.data.negative.dark } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <YesNoDialog
          onYes={() => {
            onDelete();
            setDeleteDialogOpen(false);
          }}
          onNo={() => setDeleteDialogOpen(false)}
          open={deleteDialogOpen}
          title={t("deleteDialog.title")}
          description={t("deleteDialog.description", { name: asset.name })}
          yesColor="error"
        />
        <AssetEditorDialog
          asset={asset}
          open={editDialogOpen}
          onClose={(updatedAsset) => {
            if (updatedAsset) onChange(updatedAsset);
            setEditDialogOpen(false);
          }}
        />
      </TableCell>
    </TableRow>
  );
};

export const AssetList = ({
  assets,
  total,
  onChange,
  readOnly = false,
}: {
  assets: NamedValue[];
  // what the assets come to, as the party's own line states it
  total: number;
  onChange: (updatedAssets: NamedValue[]) => void;
  // the assets are listed and cannot be changed
  readOnly?: boolean;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.parties.assets",
  });
  const [newAsset, setNewAsset] = useState<NamedValue>();

  return (
    <TableContainer
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2.25,
      }}
    >
      <Table size="small" sx={{ minWidth: theme.spacing(75) }}>
        <TableHead>
          <TableRow sx={{ bgcolor: "background.default" }}>
            <TableCell sx={COLUMN_HEADING}>{t("column.asset")}</TableCell>
            <TableCell sx={COLUMN_HEADING}>{t("column.estimates")}</TableCell>
            <TableCell sx={COLUMN_HEADING} align="right">
              {t("column.weightedValue")}
            </TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {assets.map((asset) => (
            <AssetRow
              key={asset.id}
              asset={asset}
              readOnly={readOnly}
              onChange={(newAsset) =>
                onChange(
                  assets.map((a) => (a.id === newAsset.id ? newAsset : a)),
                )
              }
              onDelete={() => onChange(assets.filter((a) => a !== asset))}
            />
          ))}
          {assets.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} sx={{ color: "text.secondary" }}>
                {t("none")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Stack
        direction="row"
        spacing={1.75}
        useFlexGap
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          px: 2,
          py: 1.25,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          disabled={readOnly}
          onClick={() =>
            setNewAsset({
              id: randomId(),
              name: "",
              values: [{ value: 0, weight: 1 }],
            })
          }
          title={t("tip.add")}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {t("addAsset")}
        </Button>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("total")}
          <Box
            component="strong"
            sx={{ ...TABULAR_NUMERALS, color: "text.primary", ml: 1 }}
          >
            {currency.format(total)}
          </Box>
        </Typography>
      </Stack>

      {newAsset && (
        <AssetEditorDialog
          asset={newAsset}
          open
          onClose={(addedAsset) => {
            if (addedAsset) onChange([...assets, addedAsset]);
            setNewAsset(undefined);
          }}
        />
      )}
    </TableContainer>
  );
};
